// src/pages/BlogContactPage.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Moon, Sun, User, Send, ArrowUpRight, Mail, Twitter, Linkedin, CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import AuthModal from "../auth/AuthModal";
import AccountManager from "../auth/AccountManager";
import { playSuccess, playError } from "../lib/audioUtils";

const BLOG_THEME_KEY = "mc_blog_theme";

const EMAIL   = "malickecase28@gmail.com";
const SOCIALS = [
  { label: "X / Twitter", short: "X",  href: "https://x.com/Malicke_msb1000",                icon: Twitter },
  { label: "LinkedIn",    short: "in", href: "https://linkedin.com/in/malicke-case-41a788177", icon: Linkedin }
];

const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "";

export default function BlogContactPage() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(BLOG_THEME_KEY) || "light";
  });

  const [form,       setForm]       = useState({ name: "", email: "", subject: "", message: "", company: "" });
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent,       setSent]       = useState(false);

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    window.scrollTo(0, 0);
    document.title = "Contact \u2022 Malicke Case";
    return () => {
      document.documentElement.classList.add("dark");
      document.title = "Malicke Case";
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(BLOG_THEME_KEY, theme); } catch (_) {}
  }, [theme]);

  const isDark = theme === "dark";
  const pageBg = isDark ? "#050506" : "#E6E2DA";

  function update(field) {
    return (e) => {
      setForm((f) => ({ ...f, [field]: e.target.value }));
      setErrors((er) => ({ ...er, [field]: "" }));
    };
  }

  function validate() {
    const e = {};
    if (!form.name.trim())    e.name    = "Please tell me your name.";
    if (!form.email.trim())   e.email   = "Email required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Hmm, that email looks off.";
    if (!form.message.trim()) e.message = "Add a message so I know how I can help.";
    setErrors(e);
    if (Object.keys(e).length > 0) playError();
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    // Honeypot  -  bots fill the hidden company field
    if (form.company) { setSubmitting(false); return; }

    setSubmitting(true);

    const formData = new FormData();
    formData.append("access_key",  WEB3FORMS_ACCESS_KEY);
    formData.append("name",        form.name.trim());
    formData.append("email",       form.email.trim());
    formData.append("replyto",     form.email.trim());
    formData.append("subject",     form.subject.trim() || `Message from ${form.name.trim()} via the blog`);
    formData.append("message",     form.message.trim());
    formData.append("from_name",   form.name.trim());

    try {
      const res  = await fetch("https://api.web3forms.com/submit", { method: "POST", body: formData });
      const data = await res.json();
      if (data.success) {
        playSuccess();
        setSent(true);
        setForm({ name: "", email: "", subject: "", message: "", company: "" });
      } else {
        playError();
        setErrors((er) => ({ ...er, _form: data.message || "Something went wrong. Try again." }));
      }
    } catch {
      playError();
      setErrors((er) => ({ ...er, _form: "Network error. Please try again." }));
    } finally {
      setSubmitting(false);
    }
  }

  // Shared input style
  function inputStyle(hasError) {
    return {
      width: "100%",
      background: "var(--blog-bg-secondary)",
      border: `0.5px solid ${hasError ? "#e55" : "var(--blog-border-primary)"}`,
      borderRadius: 6,
      padding: "10px 12px",
      fontSize: 13,
      color: "var(--blog-text-primary)",
      outline: "none",
      fontFamily: "inherit",
      boxSizing: "border-box"
    };
  }

  return (
    <div
      className="blog-theme min-h-screen"
      data-blog-theme={isDark ? "dark" : "light"}
      style={{
        background: pageBg,
        fontFamily: "'Inter', system-ui, sans-serif",
        transition: "background 200ms ease",
        padding: "32px 16px 64px"
      }}
    >
      {/* Top controls */}
      <div className="mx-auto flex justify-end gap-[10px] mb-1" style={{ width: "100%", maxWidth: 980 }}>
        <button
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className="inline-flex items-center gap-2 rounded-full px-[14px] py-[6px]"
          style={{
            fontSize: 11,
            letterSpacing: "0.06em",
            border: "1px solid var(--blog-border-primary)",
            background: "var(--blog-bg-primary)",
            color: "var(--blog-text-secondary)",
            cursor: "pointer"
          }}
        >
          {isDark ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
          {isDark ? "Light mode" : "Dark mode"}
        </button>
      </div>

      {/* Card */}
      <div
        className="mx-auto overflow-hidden"
        style={{
          width: "100%",
          maxWidth: 980,
          background: "var(--blog-bg-primary)",
          borderRadius: 12,
          border: "0.5px solid var(--blog-border-primary)",
          transition: "background 200ms ease"
        }}
      >
        {/* Nav */}
        <nav
          className="px-7 pt-[14px] pb-[14px]"
          style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}
        >
          {/* Row 1: brand + account */}
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="font-display font-medium uppercase tracking-[0.12em]"
              style={{ fontSize: 12, color: "var(--blog-text-secondary)" }}
            >
              Malicke Case
            </Link>
            {/* Desktop center links */}
            <div className="hidden sm:flex gap-6">
              <Link to="/blog/about" className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-tertiary)" }}>About</Link>
              <Link to="/blog"       className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-tertiary)" }}>Journal</Link>
              <span style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-primary)", textTransform: "uppercase" }}>Contact</span>
            </div>
            {user ? (
            <AccountManager />
            ) : (
              <button
                onClick={() => setAuthOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full"
                style={{ fontSize: 11, letterSpacing: "0.04em", padding: "6px 14px", border: "0.5px solid var(--blog-border-primary)", background: "var(--blog-bg-secondary)", color: "var(--blog-text-primary)", cursor: "pointer", fontFamily: "inherit" }}
              >
                <User className="w-3 h-3" />
                Sign in
              </button>
            )}
          </div>
          {/* Mobile-only second row: nav links centered */}
          <div className="sm:hidden flex items-center justify-center gap-5 pt-3">
            <Link to="/blog/about" className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>About</Link>
            <span style={{ color: "var(--blog-border-secondary)", fontSize: 10 }}>·</span>
            <Link to="/blog" className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>Journal</Link>
            <span style={{ color: "var(--blog-border-secondary)", fontSize: 10 }}>·</span>
            <span style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-primary)", textTransform: "uppercase" }}>Contact</span>
          </div>
        </nav>

        {/* Header */}
        <header
          className="px-6 sm:px-12 lg:px-20 pt-10 pb-8"
          style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span
              style={{ display: "inline-block", width: 18, height: "1.5px", background: "var(--blog-accent-magenta)", flexShrink: 0 }}
            />
            <span
              className="font-mono uppercase"
              style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}
            >
              Contact
            </span>
          </div>
          <h1
            className="font-display font-medium"
            style={{
              fontSize: "clamp(24px, 4vw, 38px)",
              lineHeight: 1.12,
              letterSpacing: "-0.02em",
              color: "var(--blog-text-primary)",
              maxWidth: 520
            }}
          >
            Let's talk.
          </h1>
          <p
            className="mt-3"
            style={{ fontSize: 14, lineHeight: 1.65, color: "var(--blog-text-secondary)", maxWidth: 480 }}
          >
            Whether it's a question about something I wrote, a project you think I should know about,
            or just wanting to connect, drop me a message. I read everything.
          </p>
        </header>

        {/* Body  -  two columns */}
        <div
          className="px-6 sm:px-12 lg:px-20 py-10"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 48
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 40,
              alignItems: "start"
            }}
          >
            {/* Left col wrapper via flex on larger screens */}
            <div style={{ display: "flex", gap: 48, flexWrap: "wrap", alignItems: "flex-start" }}>

              {/* Contact info */}
              <div style={{ flex: "0 0 240px", minWidth: 200 }}>
                <div
                  className="font-mono uppercase mb-4"
                  style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}
                >
                  Direct
                </div>

                <a
                  href={`mailto:${EMAIL}`}
                  className="inline-flex items-center gap-2 mb-5"
                  style={{
                    fontSize: 13,
                    color: "var(--blog-text-primary)",
                    textDecoration: "none"
                  }}
                >
                  <Mail className="w-3.5 h-3.5" style={{ color: "var(--blog-accent-teal)", flexShrink: 0 }} />
                  {EMAIL}
                </a>

                <div className="mt-6">
                  <div
                    className="font-mono uppercase mb-3"
                    style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}
                  >
                    Elsewhere
                  </div>
                  <div className="flex flex-col gap-2">
                    {SOCIALS.map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2"
                        style={{ fontSize: 13, color: "var(--blog-text-secondary)", textDecoration: "none" }}
                      >
                        <s.icon className="w-3.5 h-3.5" style={{ color: "var(--blog-text-tertiary)", flexShrink: 0 }} />
                        {s.label}
                        <ArrowUpRight className="w-3 h-3 opacity-40" />
                      </a>
                    ))}
                  </div>
                </div>

                <div
                  className="mt-8 pt-6"
                  style={{ borderTop: "0.5px solid var(--blog-border-secondary)" }}
                >
                  <a
                    href="/resume.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono uppercase inline-flex items-center gap-1"
                    style={{ fontSize: 10, letterSpacing: "0.12em", color: "var(--blog-text-tertiary)", textDecoration: "none" }}
                  >
                    Resume (PDF)
                    <ArrowUpRight className="w-3 h-3" />
                  </a>
                </div>
              </div>

              {/* Right col  -  form */}
              <div style={{ flex: "1 1 300px", minWidth: 260 }}>
                <div
                  className="font-mono uppercase mb-4"
                  style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}
                >
                  Send a message
                </div>

                {sent ? (
                  <div
                    style={{
                      border: "0.5px solid var(--blog-border-secondary)",
                      borderRadius: 8,
                      padding: "24px 20px",
                      background: "var(--blog-bg-secondary)",
                      textAlign: "center"
                    }}
                  >
                    <CheckCircle2 className="w-6 h-6 mx-auto mb-3" style={{ color: "var(--blog-accent-teal)" }} />
                    <div
                      className="font-display font-medium mb-2"
                      style={{ fontSize: 16, color: "var(--blog-text-primary)" }}
                    >
                      Got it, thanks.
                    </div>
                    <p style={{ fontSize: 13, color: "var(--blog-text-tertiary)", lineHeight: 1.6, margin: 0 }}>
                      I'll get back to you soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                    {/* Honeypot  -  hidden from humans, catches bots */}
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={update("company")}
                      style={{ display: "none" }}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                    />

                    {/* Name */}
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="font-mono uppercase"
                        style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)", display: "block", marginBottom: 6 }}
                      >
                        Name
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        autoComplete="name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Your name"
                        style={inputStyle(!!errors.name)}
                      />
                      {errors.name && (
                        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#e55" }}>{errors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="font-mono uppercase"
                        style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)", display: "block", marginBottom: 6 }}
                      >
                        Email
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@domain.com"
                        style={inputStyle(!!errors.email)}
                      />
                      {errors.email && (
                        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#e55" }}>{errors.email}</p>
                      )}
                    </div>

                    {/* Subject */}
                    <div>
                      <label
                        htmlFor="contact-subject"
                        className="font-mono uppercase"
                        style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)", display: "block", marginBottom: 6 }}
                      >
                        Subject <span style={{ opacity: 0.5 }}>(optional)</span>
                      </label>
                      <input
                        id="contact-subject"
                        type="text"
                        value={form.subject}
                        onChange={update("subject")}
                        placeholder="Post feedback, topic ideas, anything"
                        style={inputStyle(false)}
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label
                        htmlFor="contact-message"
                        className="font-mono uppercase"
                        style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)", display: "block", marginBottom: 6 }}
                      >
                        Message
                      </label>
                      <textarea
                        id="contact-message"
                        rows={5}
                        value={form.message}
                        onChange={update("message")}
                        placeholder="React to a post, share what you're building, or just say hello."
                        style={{ ...inputStyle(!!errors.message), resize: "vertical" }}
                      />
                      {errors.message && (
                        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#e55" }}>{errors.message}</p>
                      )}
                    </div>

                    {errors._form && (
                      <p style={{ fontSize: 12, color: "#e55", margin: 0 }}>
                        {errors._form}{" "}
                        <a href={`mailto:${EMAIL}`} style={{ color: "inherit" }}>Email me directly.</a>
                      </p>
                    )}

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <p style={{ fontSize: 11, color: "var(--blog-text-tertiary)", margin: 0 }}>
                        No newsletter. No spam. I'll just reply.
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center justify-center gap-2 rounded-md"
                        style={{
                          background: "var(--blog-text-primary)",
                          color: "var(--blog-bg-primary)",
                          border: "none",
                          padding: "11px 20px",
                          fontSize: 12,
                          fontWeight: 500,
                          letterSpacing: "0.04em",
                          cursor: submitting ? "not-allowed" : "pointer",
                          opacity: submitting ? 0.6 : 1,
                          fontFamily: "inherit",
                          transition: "opacity 150ms ease",
                          flexShrink: 0
                        }}
                      >
                        {submitting
                          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                          : <><Send className="w-3.5 h-3.5" /> Send message</>
                        }
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div
        className="mx-auto mt-4 flex items-center justify-between flex-wrap gap-3"
        style={{ width: "100%", maxWidth: 980 }}
      >
        <Link
          to="/blog"
          className="font-mono uppercase inline-flex items-center gap-1"
          style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--blog-text-tertiary)" }}
        >
          ← Back to Journal
        </Link>
        <a
          href="/feed.xml"
          className="font-mono uppercase inline-flex items-center gap-1"
          style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--blog-text-tertiary)" }}
        >
          RSS feed
          <ArrowUpRight className="w-3 h-3" />
        </a>
      </div>

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultMode="login"
      />
    </div>
  );
}
